import type { PlatformCreativeConstraints } from '../../../../logic/platform-constraints';

/**
 * Headlines and descriptions are two independent asset pools — Meta
 * (`asset_feed_spec`) and Google (RSA/RDA) both mix them freely at serve time,
 * so the editor renders and manages each pool on its own instead of pairing
 * entries into "Variation N" cards.
 */

/** Cap to use when no platform is picked yet (draft opened mid-wizard). */
export const DEFAULT_FIELD_MAX = 5;

/** Which pool a list is editing. */
export type CopyField = 'headlines' | 'descriptions';

export function getFieldMax(
  constraints: PlatformCreativeConstraints | null,
  field: CopyField,
): number {
  return constraints ? constraints[field].max : DEFAULT_FIELD_MAX;
}

export function getFieldMin(
  constraints: PlatformCreativeConstraints | null,
  field: CopyField,
): number {
  return constraints ? constraints[field].min : 1;
}

/**
 * Grows `values` to `length` with empty strings and flattens any hole.
 *
 * Holes are not cosmetic: `mapWizardPayloadToCreateCampaign` does
 * `headlines.map((h) => h.trim())`, so a single `undefined` slot throws at
 * launch. Every write site pads through here.
 */
export function padTo(values: string[], length: number): string[] {
  const cleaned = values.map((value) => value ?? '');
  if (cleaned.length >= length) return cleaned;
  return [...cleaned, ...Array.from({ length: length - cleaned.length }, () => '')];
}

/** A creative always needs at least one entry per pool. */
export function ensureAtLeastOne(values: string[]): string[] {
  return values.length > 0 ? values : [''];
}

/** Per-field hint line ("1-5 headlines — max 40 characters each."). */
export function buildFieldHelpText(
  constraints: PlatformCreativeConstraints | null,
  field: CopyField,
): string {
  const singular = field === 'headlines' ? 'headline' : 'description';
  const plural = `${singular}s`;

  if (!constraints) {
    return `Up to ${DEFAULT_FIELD_MAX} ${plural}.`;
  }

  const { min, max, maxLength } = constraints[field];
  const countLabel =
    min === max ? `${max} ${max === 1 ? singular : plural}` : `${min}-${max} ${plural}`;
  return `${countLabel} — max ${maxLength} characters each.`;
}
